import { useCallback, useEffect, useState } from "react";
import { FilmProfile } from "../../../models/film-profile.model";
import { useNavigate, useParams } from "react-router-dom";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { useAtom } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import useModal from "antd/es/modal/useModal";
import { colors } from "../../../theme/colors";
import { Row } from "../../../components/ui/grid/row.component";
import { Button, Checkbox, Form, Input, Modal, Select, Tabs } from "antd";
import styled from "styled-components";
import { Subtitle } from "../components/subtitle.component";
import { TextButton } from "../../../components/ui/text-button/text-button.component";
import { MdAdd } from "react-icons/md";
import { countries } from "../../../data/countries";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { useForm } from "antd/es/form/Form";
import axiosLib, { AxiosError } from "axios";
import toast from "react-hot-toast";
import { v4 } from "uuid";
import { jsonToFormData } from "../../../utils/functions/misc/jsonToFormData";
import { FileUpload } from "../../../components/ui/file-upload/file-upload.component";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { wait } from "../../../utils/functions/misc/wait";
import { config } from "../../../config/config";
import { SetState } from "../../../types/SetState";
import { Distribution } from "../../../models/distribution.model";

const DistributeFormItem = styled(Form.Item).attrs({ layout: "vertical" })`
  width: 100%;
  max-width: 100%;
  min-width: 0 !important;
  flex: none !important;
  margin-bottom: 1.5rem !important;

  .ant-form-item-label > label {
    font-size: 1.35rem;
    font-weight: 600;
    color: ${colors.text.hex};
    height: auto;
  }
`;

/** Block wrapper — avoid nesting flex Row around Ant Design form rows (causes collapsed/overlapping layout). */
const FieldStack = styled.div`
  width: 100%;
  display: block;
`;

const PlatformCheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
`;

interface FormSchema {
  territory: string;
  territory_countries?: string[];
  platforms: string[];
  monetization_model: string[];
  information_true: boolean;
  terms_and_conditions: boolean;
  original_content: boolean;
  signature: string;
  project_id?: string;
  project_type?: "film" | "series";
  subtitle_status?: string;
}

export const DistributeFilm = () => {
  const { customURL: filmId } = useParams();

  const [auth, setAuth] = useAtom(authAtom);

  const navigate = useNavigate();

  const [modal, modalConfig] = useModal();

  const [distributionId, setDistributionId] = useState<string | null>(null);
  const [distribution, setDistribution] = useState<Distribution | null>(null);

  const [isMetadataSubmitted, setIsMetadataSubmitted] = useState(false);

  const [activeTab, setActiveTab] = useState(1);

  const [showSubmitAsModal, setShowSubmitAsModal] = useState(false);

  const [film, setFilm] = useState<FilmProfile | null>(null);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const [isSubtitlesAvailable, setIsSubtitlesAvailable] = useState(false);
  const [subtitles, setSubtitles] = useState<
    { language: string; file: File | string | null; id?: number }[]
  >([]);

  const [territoriesType, setTerritoriesType] = useState<
    null | "worldwide" | "excluding_these_countries" | "only_these_countries"
  >(null);

  const [trailer, setTrailer] = useState<File | null | string>(null);
  const [trailerUploaded, setTrailerUploaded] = useState<
    number | boolean | null
  >(null);
  const [trailerAbort, setTrailerAbort] = useState<AbortController | null>(
    null
  );

  const [filmVideo, setFilmVideo] = useState<File | null | string>(null);
  const [filmVideoUploaded, setFilmVideoUploaded] = useState<
    number | boolean | null
  >(null);
  const [filmVideoAbort, setFilmVideoAbort] = useState<AbortController | null>(
    null
  );

  const [checkboxes, setCheckboxes] = useState([false, false, false]);

  const [verticalTrailer, setVerticalTrailer] = useState<File | null | string>(
    null
  );
  const [verticalTrailerUploaded, setVerticalTrailerUploaded] = useState<
    number | boolean | null
  >(null);
  const [verticalTrailerAbort, setVerticalTrailerAbort] =
    useState<AbortController | null>(null);

  const [verticalVideo, setVerticalVideo] = useState<File | null | string>(
    null
  );
  const [verticalVideoUploaded, setVerticalVideoUploaded] = useState<
    number | boolean | null
  >(null);
  const [verticalVideoAbort, setVerticalVideoAbort] =
    useState<AbortController | null>(null);

  const [censorCertificate, setCensorCertificate] = useState<
    File | string | null
  >(null);
  const [musicCueSheet, setMusicCueSheet] = useState<File | string | null>(
    null
  );

  const [agreementUrl, setAgreementUrl] = useState<string | null>(null);

  const [platforms, setPlatforms] = useState<string[]>([]);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const [form] = useForm<FormSchema>();
  const [form2] = useForm<{ subtitle_status: string }>();

  const onSubmitAsConfirmed = useCallback(
    async (type: "individual" | "business") => {
      const formData = new FormData();
      formData.append("submit_as", type);

      const result = await axios.put("/auth/update/user/profile", formData);
      handleResponse(result, () => {
        setAuth({
          ...auth,
          user: auth.user
            ? {
              ...auth.user,
              submit_as: type,
            }
            : auth.user,
        });
      });
    },
    [auth]
  );

  const uploadFile = useCallback(
    async (
      newTrailer: File,
      video_type: "video" | "vertical_video" | "trailer" | "vertical_trailer",
      onProgress?: (progress: number) => void,
      setAbort?: SetState<AbortController | null>
    ) => {
      try {
        if (!distributionId) return false;

        const abortController = new AbortController();
        if (setAbort) {
          setAbort(abortController);
        }

        const {
          data: { upload_id, part_urls: partUploadUrls, file_name },
        } = await axios.post<{
          upload_id: string;
          part_urls: { part_number: number; url: string }[];
          file_name: string;
        }>("/client/distribution/multipart-video", {
          video_type,
          distribution_id: distributionId,
          original_file_name: newTrailer.name,
          file_size: newTrailer.size,
        });

        const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk
        const totalParts = Math.ceil(newTrailer.size / CHUNK_SIZE);
        const progressTracker = Array(totalParts).fill(0); // Track progress of each part

        const sleep = (ms: number) =>
          new Promise((resolve) => setTimeout(resolve, ms)); // Cooldown helper

        const uploadPart = async (
          partNumber: number
        ): Promise<{ ETag: string; PartNumber: number }> => {
          const { url } = partUploadUrls[partNumber];
          const start = partNumber * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, newTrailer.size);
          const blobPart = newTrailer.slice(start, end);

          let retries = 5;
          while (retries > 0) {
            try {
              const { headers } = await axios.put(url, blobPart, {
                headers: { "Content-Type": "application/octet-stream" },
                signal: abortController.signal,
                onUploadProgress: (progressEvent) => {
                  if (onProgress) {
                    progressTracker[partNumber] =
                      progressEvent.loaded / blobPart.size;
                    const overallProgress =
                      progressTracker.reduce((sum, val) => sum + val, 0) /
                      totalParts;
                    onProgress(overallProgress >= 0.99 ? 1 : overallProgress);
                  }
                },
              });

              return {
                ETag: headers.etag as string,
                PartNumber: partNumber + 1,
              };
            } catch (err) {
              if (axiosLib.isCancel(err)) {
                throw new Error("Upload aborted");
              }

              if (
                axiosLib.isAxiosError(err) &&
                (err.code === "ECONNRESET" ||
                  (err.message && err.message.includes("ERR_CONNECTION_RESET")))
              ) {
                retries -= 1;
                console.warn(
                  `Retrying part ${partNumber + 1
                  } after cooldown. Attempts left: ${retries}`
                );
                if (retries === 0) {
                  throw new Error(
                    `Failed to upload part ${partNumber + 1
                    }: ERR_CONNECTION_RESET`
                  );
                }
                await sleep(2000);
              } else {
                throw err;
              }
            }
          }
          throw new Error(`Failed to upload part ${partNumber + 1}`);
        };

        const concurrentLimit = 3;
        const partETags: { ETag: string; PartNumber: number }[] = [];
        const pool = Array(concurrentLimit).fill(null); // Create a pool

        let index = 0;

        await Promise.all(
          pool.map(async () => {
            while (index < totalParts) {
              const currentIndex = index;
              index += 1;
              const partETag = await uploadPart(currentIndex);
              if (partETag) {
                partETags.push(partETag);
              }
            }
          })
        );

        await axios.post("/client/complete/distribution/multipart-video", {
          upload_id,
          parts: partETags.sort((a, b) => a.PartNumber - b.PartNumber),
          key: file_name,
        });

        // After successful upload, fetch updated distribution data
        const { data: updatedDistribution } = await axios.get<
          FormSchema & Distribution
        >("/client/distribution", {
          headers: {
            "X-Project-Id": filmId,
            "X-Project-Type": "film",
          },
        });
        setDistribution(updatedDistribution);

        return true;
      } catch (e) {
        if (axiosLib.isCancel(e)) {
          // console.log("Upload aborted");
        } else if (axiosLib.isAxiosError(e)) {
          console.error("Upload failed with Axios error:", e.message);
        } else {
          handleResponse(e as Error);
        }
        return false;
      }
    },
    [distributionId, filmId]
  );

  useEffect(() => {
    axios
      .get<FilmProfile>("/client/film", {
        headers: {
          "X-FILM-ID": filmId,
        },
      })
      .then((response) => {
        setFilm(response.data);
      })
      .catch(handleResponse);

    axios
      .get<FormSchema & Distribution>("/client/distribution", {
        headers: {
          "X-Project-Id": filmId,
          "X-Project-Type": "film",
        },
      })
      .then((resp) => {
        setIsMetadataSubmitted(true);
        setActiveTab(2);
        setDistributionId(resp.data.id);
        setDistribution(resp.data);
        setPlatforms(resp.data.platforms ?? []);
        setTerritoriesType(
          resp.data.territory as
          | "worldwide"
          | "excluding_these_countries"
          | "only_these_countries"
        );
        const subtitles = resp.data.videos.reduce(
          (list, { subtitles }) => [...list, ...(subtitles ?? [])],
          [] as { file: string; language: string; id: number }[]
        );
        setSubtitles(subtitles);

        if (resp.data.subtitle_status) {
          form2.setFieldValue("subtitle_status", resp.data.subtitle_status);
        }
        if (resp.data.subtitle_status === "available") {
          setIsSubtitlesAvailable(true);
        }
        if (resp.data.agreement_url) {
          setAgreementUrl(resp.data.agreement_url);
        }
        const primaryVideo = resp.data.videos.find(
          (video) => video.video_type === "primary-video"
        ),
          additionalVideo = resp.data.videos.find(
            (video) => video.video_type === "additional-video"
          ),
          primaryTrailer = resp.data.videos.find(
            (video) => video.video_type === "primary-trailer"
          ),
          additionalTrailer = resp.data.videos.find(
            (video) => video.video_type === "additional-trailer"
          );
        const storedName = (video?: { video_id?: string | null; video_file_name?: string | null }) =>
          video?.video_id || video?.video_file_name || null;
        if (primaryVideo) setFilmVideo(storedName(primaryVideo));
        if (additionalVideo) setVerticalVideo(storedName(additionalVideo));
        if (primaryTrailer) setTrailer(storedName(primaryTrailer));
        if (additionalTrailer) setVerticalTrailer(storedName(additionalTrailer));

        if (
          resp.data.trailers?.find(
            (trailer) => trailer.type === "horizontal_trailer"
          )
        ) {
          setTrailer(
            resp.data.trailers?.find(
              (trailer) => trailer.type === "horizontal_trailer"
            )!.video_file_name
          );
        }
        if (
          resp.data.trailers?.find(
            (trailer) => trailer.type === "vertical_trailer"
          )
        ) {
          setVerticalTrailer(
            resp.data.trailers?.find(
              (trailer) => trailer.type === "vertical_trailer"
            )!.video_file_name
          );
        }
        if (resp.data.censor_certificate) {
          setCensorCertificate(resp.data.censor_certificate);
        }
        if (resp.data.music_cue_sheet) {
          setMusicCueSheet(resp.data.music_cue_sheet);
        }

        const data = {
          ...resp.data,
          information_true: true,
          terms_and_conditions: true,
          original_content: true,
        };
        if (data.territory_countries)
          data.territory_countries = data.territory_countries
            .map(
              (code) =>
                countries.find(
                  ({ value }) =>
                    value.split("-")[1].toLowerCase() ===
                    code.toUpperCase()?.toLowerCase()
                )?.value
            )
            .filter((el) => !!el) as string[];
        data.monetization_model = data.monetization_models ?? [];

        form.setFieldsValue(data);
        setCheckboxes([true, true, true]);
      })
      .catch(() => null);
  }, [filmId, form]);

  useEffect(() => {
    if (auth.user && !hasShownPopup) {
      // IMPORTANT uncomment once the backend issue with update persistence is fixed
      if (
        !auth.user.address_line1 &&
        (!auth.user.phone_number || !auth.user.phone_code)
      ) {
        toast.error(
          "Please enter an address and phone number for your profile before submitting a distribution!"
        );
        wait(500).then(() =>
          navigate(`/profile?on_submit=/f/${filmId}/distribute`)
        );
        return;
      } else if (!auth.user.address_line1) {
        toast.error(
          "Please enter an address for your profile before submitting a distribution!"
        );
        wait(500).then(() =>
          navigate(`/profile?on_submit=/f/${filmId}/distribute`)
        );
        return;
      } else if (!auth.user.phone_number || !auth.user.phone_code) {
        toast.error(
          "Please enter a phone number for your profile before submitting a distribution!"
        );
        wait(500).then(() =>
          navigate(`/profile?on_submit=/f/${filmId}/distribute`)
        );
        return;
      } else if (!auth.user.phone_verified) {
        toast.error(
          "Please verify your phone number for your profile before submitting a distribution!"
        );
        wait(500).then(() =>
          window.location.replace(
            `${config.auth_url}/verify?source=distribute&r=${config.base_url}/f/${filmId}/distribute`
          )
        );
      }

      if (auth.user.submit_as === "unclassified") {
        setHasShownPopup(true);
        setShowSubmitAsModal(true);
      }
    }
  }, [auth, hasShownPopup]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowY: html.style.overflowY,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyOverflowY: body.style.overflowY,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow ?? "",
      rootHeight: root?.style.height ?? "",
      rootMinHeight: root?.style.minHeight ?? "",
    };
    html.style.overflowY = "auto";
    html.style.height = "auto";
    body.style.overflowY = "auto";
    body.style.height = "auto";
    if (root) {
      root.style.overflow = "visible";
      root.style.height = "auto";
      root.style.minHeight = "100%";
    }
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowY = prev.htmlOverflowY;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
        root.style.minHeight = prev.rootMinHeight;
      }
    };
  }, []);

  const distributionVideoPreview = (
    videoType:
      | "primary-video"
      | "primary-trailer"
      | "additional-video"
      | "additional-trailer"
  ) => {
    const entry = distribution?.videos?.find((v) => v.video_type === videoType);
    if (!entry?.video_id) return null;
    if (!entry.is_encoded) {
      return <ProcessingText>Processing...</ProcessingText>;
    }
    const id = entry.video_id;
    if (videoType === "primary-video" && id.startsWith("https")) {
      return (
        <VideoFrame>
          <video src={id} />
        </VideoFrame>
      );
    }
    return (
      <VideoFrame>
        <iframe
          src={`https://iframe.mediadelivery.net/embed/102339/${id}?autoplay=false`}
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
        />
      </VideoFrame>
    );
  };

  return (
    <Main>
      <BackButton />
      {modalConfig}
      <Modal
        closable={false}
        open={showSubmitAsModal}
        title="Submit as individual / business"
        footer={
          <Row
            $full
            $gap={10}
            style={{ justifyContent: "flex-end", marginTop: 20 }}
          >
            <Button
              type="dashed"
              onClick={() => {
                onSubmitAsConfirmed("individual");
                setShowSubmitAsModal(false);
              }}
            >
              Individual
            </Button>
            <Button
              type="primary"
              onClick={() => {
                axios
                  .get("/auth/business/profile")
                  .then(() => {
                    onSubmitAsConfirmed("business");
                    setShowSubmitAsModal(false);
                  })
                  .catch((e) => {
                    setShowSubmitAsModal(false);
                    navigate(`/b?r=/f/${filmId}/distribute`);
                  });
              }}
            >
              Business
            </Button>
          </Row>
        }
      >
        Do you want to submit this and future distributions as a business or as
        an individual?
        <br />
        <br />
        <span style={{ color: colors.error.hex }}>
          NOTE: THIS CHOICE CANNOT BE CHANGED.
        </span>
      </Modal>
      <DistributeHero>
        <HeroTop>
          <Title>Submit for distribution</Title>
          {agreementUrl ? (
            <Button type="default" size="large" onClick={() => window.open(agreementUrl)}>
              Open agreement
            </Button>
          ) : null}
        </HeroTop>
        <Paragraph>
          You are submitting <FilmName>{film?.title ?? "Untitled film"}</FilmName>.
          After uploading files, wait until each upload finishes. Do not reload or
          close this page, or turn off your device, while uploads are in progress.
        </Paragraph>
      </DistributeHero>
      <Tabs
        className="distribute-tabs"
        destroyInactiveTabPane
        animated={{ inkBar: true, tabPane: false }}
        // activeKey={isMetadataSubmitted ? "2" : "1"}
        activeKey={activeTab.toString()}
        onChange={(activeKey) => setActiveTab(+activeKey)}
        items={[
          {
            key: "1",
            label: "Rights Management",
            // disabled: isMetadataSubmitted,
            children: (
              <Form
                layout="vertical"
                form={form}
                className="distribute-form distribute-rights-form"
              >
                <RightsShell>
                  <RightsColumn>
                  <FieldStack>
                    <DistributeFormItem
                      label="Territory"
                      name="territory"
                      required
                      rules={[
                        {
                          required: true,
                          message: "Please select a territory option!",
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder="Select a territory management option"
                        disabled={isMetadataSubmitted}
                        showSearch
                        onSelect={(type) => {
                          setTerritoriesType(type);
                        }}
                        options={[
                          { label: "Worldwide", value: "worldwide" },
                          {
                            label: "Worldwide, excluding select countries",
                            value: "excluding_these_countries",
                          },
                          {
                            label: "Only select countries",
                            value: "only_these_countries",
                          },
                        ]}
                      />
                    </DistributeFormItem>
                  </FieldStack>
                  {territoriesType !== "worldwide" ? (
                    <>
                      <FieldStack>
                        <DistributeFormItem
                          name="territory_countries"
                          label={`Countries to ${territoriesType
                            ? territoriesType === "only_these_countries"
                              ? "include"
                              : "exclude"
                            : "include/exclude"
                            }`}
                          required
                          rules={[
                            {
                              required: true,
                              message:
                                "Please select countries to include/exclude!",
                            },
                          ]}
                        >
                          <Select
                            size="large"
                            disabled={isMetadataSubmitted}
                            placeholder={`Select countries to ${territoriesType
                              ? territoriesType === "only_these_countries"
                                ? "include"
                                : "exclude"
                              : "include/exclude"
                              }`}
                            showSearch
                            allowClear
                            options={countries}
                            mode="multiple"
                          />
                        </DistributeFormItem>
                      </FieldStack>
                    </>
                  ) : null}
                  <FieldStack>
                    <DistributeFormItem
                      label="Monetisation model"
                      name="monetization_model"
                      required
                      rules={[
                        {
                          required: true,
                          message: "Please select a monetisation model!",
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder="Select monetisation model"
                        showSearch
                        disabled={isMetadataSubmitted}
                        options={[
                          {
                            label: "AVOD (advertising-based video on demand)",
                            value: "avod",
                          },
                        ]}
                      />
                    </DistributeFormItem>
                  </FieldStack>
                  <FieldStack>
                    <DistributeFormItem
                      label="Platforms"
                      name="platforms"
                      initialValue={["pf_owned"]}
                    >
                      <Checkbox.Group defaultValue={["pf_owned"]}>
                        <PlatformCheckList>
                          <Checkbox value="pf_owned" disabled checked>
                            PF owned platforms (its website, mobile apps, apps
                            on other platforms, app-in-app, integrations, etc.)
                          </Checkbox>
                          <Checkbox
                            value="pf_digital"
                            disabled={isMetadataSubmitted}
                          >
                            All digital platforms including but not limited to
                            internet, mobile, IPTV, etc.
                          </Checkbox>
                          <Checkbox
                            value="pf_branded"
                            disabled={isMetadataSubmitted}
                          >
                            PF branded channels on other platforms including but
                            not limited to YouTube, Facebook, etc.
                          </Checkbox>
                          <Checkbox
                            value="pf_syndication"
                            disabled={isMetadataSubmitted}
                          >
                            Syndication across platforms excluding YouTube and
                            Facebook.
                          </Checkbox>
                          <Checkbox value="pf_tv" disabled={isMetadataSubmitted}>
                            TV and broadcast
                          </Checkbox>
                          <Checkbox
                            value="pf_theatre"
                            disabled={isMetadataSubmitted}
                          >
                            Theatrical, festival, public/private screenings
                          </Checkbox>
                        </PlatformCheckList>
                      </Checkbox.Group>
                    </DistributeFormItem>
                  </FieldStack>
                  </RightsColumn>
                  <RightsColumn>
                    <AckPanel>
                    <AckHeading>
                      <RequiredStar>*</RequiredStar>
                      Acknowledgement
                    </AckHeading>
                    <FieldStack>
                      <DistributeFormItem name="information_true">
                        <Checkbox
                          disabled={isMetadataSubmitted}
                          checked={checkboxes[0]}
                          onChange={(evt) => {
                            form.setFieldValue(
                              "information_true",
                              evt.target.checked
                            );

                            const newCheckboxes = [...checkboxes];
                            newCheckboxes[0] = evt.target.checked;

                            setCheckboxes(newCheckboxes);
                          }}
                        >
                          All information submitted above is true, correct and to
                          the best of my/our knowledge. I/We further accept
                          responsibility for the accuracy of all the information
                          provided by me/us.
                        </Checkbox>
                      </DistributeFormItem>
                    </FieldStack>
                    <FieldStack>
                      <DistributeFormItem name="terms_and_conditions">
                        <Checkbox
                          disabled={isMetadataSubmitted}
                          checked={checkboxes[1]}
                          onChange={(evt) => {
                            form.setFieldValue(
                              "terms_and_conditions",
                              evt.target.checked
                            );

                            const newCheckboxes = [...checkboxes];
                            newCheckboxes[1] = evt.target.checked;

                            setCheckboxes(newCheckboxes);
                          }}
                        >
                          I/We hereby declare that we have read the rules, terms
                          and regulations of the Distribution and hereby agree and
                          accept the same.
                        </Checkbox>
                      </DistributeFormItem>
                    </FieldStack>
                    <FieldStack>
                      <DistributeFormItem name="original_content">
                        <Checkbox
                          disabled={isMetadataSubmitted}
                          checked={checkboxes[2]}
                          onChange={(evt) => {
                            form.setFieldValue(
                              "original_content",
                              evt.target.checked
                            );
                            const newCheckboxes = [...checkboxes];
                            newCheckboxes[2] = evt.target.checked;

                            setCheckboxes(newCheckboxes);
                          }}
                        >
                          I/We hereby declare that the content is original and
                          I/We am/are authorised to deal with the above content,
                          enter into this agreement by accepting all the terms and
                          rules including on behalf of the other right holders, if
                          any. Signature
                        </Checkbox>
                      </DistributeFormItem>
                    </FieldStack>
                    <FieldStack>
                      <DistributeFormItem
                        label="Signature"
                        name="signature"
                        required
                        rules={[{ required: true }]}
                      >
                        <Input
                          size="large"
                          disabled={isMetadataSubmitted}
                          placeholder="Type in your signature"
                          style={{ fontWeight: "600" }}
                        />
                      </DistributeFormItem>
                    </FieldStack>
                    <RightsSubmit>
                      <SubmitButton
                        variant="inline"
                        label="Save rights & continue"
                        disabled={isMetadataSubmitted}
                        onClick={async () => {
                          try {
                            form.submit();
                            await form.validateFields();

                            const rightsManagementBody = form.getFieldsValue();
                            if (
                              !rightsManagementBody.information_true ||
                              !rightsManagementBody.original_content ||
                              !rightsManagementBody.terms_and_conditions
                            )
                              return toast.error(
                                "Please check all the required acknowledgement checkboxes!"
                              );
                            if (!rightsManagementBody.signature)
                              return toast.error("Please enter your signature!");

                            modal.confirm({
                              title: "Submit Rights Management",
                              content:
                                "Please confirm if you want to submit rights management details and move on to the next step to upload assets. You will be able to edit, if necessary, after the upload is completed.",
                              okText: "Submit and continue",
                              onOk: () => {
                                rightsManagementBody.project_id = filmId;
                                rightsManagementBody.project_type = "film";
                                rightsManagementBody.territory_countries = (
                                  rightsManagementBody.territory_countries ?? []
                                ).map((country) =>
                                  country.split("-")[1].toLowerCase()
                                );

                                axios
                                  .post<{ id: string }>(
                                    "/client/distribution",
                                    rightsManagementBody
                                  )
                                  .then((resp) => {
                                    toast.success(
                                      "Rights data saved successfully!"
                                    );
                                    setIsMetadataSubmitted(true);
                                    setDistributionId(resp.data.id);
                                    setActiveTab(2);
                                  })
                                  .catch(handleResponse);
                              },
                            });
                          } catch (e) {
                            if (e instanceof AxiosError) {
                              handleResponse(e);
                            } else {
                              toast.error("Please fix the errors in the form!");
                            }
                          }
                        }}
                      />
                    </RightsSubmit>
                    </AckPanel>
                  </RightsColumn>
                </RightsShell>
              </Form>
            ),
          },
          {
            key: "2",
            label: "Uploads",
            disabled: !isMetadataSubmitted,
            children: (
              <>
                <Form
                  layout="vertical"
                  form={form2}
                  className="distribute-form distribute-uploads-form"
                >
                  <UploadShell className="distribute-uploads">
                    <UploadBoard>
                      <UploadSection $gridArea="videos">
                        <UploadSectionTitle>Videos</UploadSectionTitle>
                        <UploadSectionHint>
                          Feature master and trailers. Optional rows are for
                          alternate or vertical cuts—leave blank if not needed.
                        </UploadSectionHint>
                        <VideoQuadGrid>
                          <VideoField>
                            <DistributeFormItem
                              label="Primary Video"
                              required
                              name="filmVideo"
                            >
                              <FileUpload variant="dropzone"
                                dropzonePreview={distributionVideoPreview(
                                  "primary-video"
                                )}
                                file={filmVideo instanceof File ? filmVideo : null}
                                fileUrl={
                                  distribution?.videos?.find(
                                    (video) => video.video_type === "primary-video"
                                  )?.video_id
                                    ? `https://iframe.mediadelivery.net/embed/102339/${distribution.videos.find(
                                      (video) =>
                                        video.video_type === "primary-video"
                                    )!.video_id
                                    }?autoplay=false`
                                    : null
                                }
                                fileName={
                                  distribution?.videos.find(
                                    (video) => video.video_type === "primary-video"
                                  )?.video_file_name ??
                                  (typeof filmVideo === "string"
                                    ? filmVideo
                                    : undefined)
                                }
                                uploaded={filmVideoUploaded}
                                onChange={async (newTrailer) => {
                                  // Reset states when new file is selected
                                  if (newTrailer) {
                                    toast.success(
                                      "Uploading video... Please wait."
                                    );
                                    setFilmVideoUploaded(0); // Start with 0 progress
                                    setFilmVideo(newTrailer);

                                    // Clear existing video from distribution state
                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "primary-video"
                                      );
                                      setDistribution(newDistribution);
                                    }

                                    if (filmVideoAbort) {
                                      filmVideoAbort.abort();
                                    }

                                    if (!distributionId) return;

                                    setIsSubmitDisabled(true);
                                    const uploadSuccessful = await uploadFile(
                                      newTrailer,
                                      "video",
                                      (progress) => {
                                        setFilmVideoUploaded(progress);
                                      },
                                      setFilmVideoAbort
                                    );
                                    setIsSubmitDisabled(false);

                                    if (uploadSuccessful) {
                                      setFilmVideoUploaded(true);
                                      toast.success(
                                        "Video uploaded and saved to distribution successfully!"
                                      );

                                      // Fetch updated distribution data to get new video details
                                      const { data: updatedDistribution } =
                                        await axios.get<FormSchema & Distribution>(
                                          "/client/distribution",
                                          {
                                            headers: {
                                              "X-Project-Id": filmId,
                                              "X-Project-Type": "film",
                                            },
                                          }
                                        );
                                      setDistribution(updatedDistribution);
                                    } else {
                                      setFilmVideoUploaded(false);
                                      setFilmVideo(null);
                                    }
                                  } else {
                                    // Handle file removal
                                    setFilmVideo(null);
                                    setFilmVideoUploaded(null);
                                    if (filmVideoAbort) filmVideoAbort.abort();

                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "primary-video"
                                      );
                                      setDistribution(newDistribution);
                                    }
                                  }
                                }}
                                accept="video/mp4"
                              />
                            </DistributeFormItem>
                          </VideoField>
                          <VideoField>
                            <DistributeFormItem label="Primary Trailer" name="filmTrailer">
                              <FileUpload variant="dropzone"
                                dropzonePreview={distributionVideoPreview(
                                  "primary-trailer"
                                )}
                                file={trailer instanceof File ? trailer : null}
                                fileUrl={
                                  distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "primary-trailer"
                                  )?.video_id
                                    ? `https://iframe.mediadelivery.net/embed/102339/${distribution.videos.find(
                                      (video) =>
                                        video.video_type === "primary-trailer"
                                    )!.video_id
                                    }?autoplay=false`
                                    : null
                                }
                                fileName={
                                  distribution?.videos.find(
                                    (video) =>
                                      video.video_type === "primary-trailer"
                                  )?.video_file_name ??
                                  (typeof trailer === "string"
                                    ? trailer
                                    : undefined)
                                }
                                uploaded={trailerUploaded}
                                onChange={async (newTrailer) => {
                                  if (newTrailer) {
                                    toast.success(
                                      "Uploading video... Please wait."
                                    );
                                    setTrailerUploaded(false);
                                    setTrailer(newTrailer);

                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "primary-trailer"
                                      );
                                      setDistribution(newDistribution);
                                    }

                                    if (trailerAbort) {
                                      trailerAbort.abort();
                                    }

                                    if (!distributionId) return;

                                    setIsSubmitDisabled(true);
                                    const uploadSuccessful = await uploadFile(
                                      newTrailer,
                                      "trailer",
                                      (progress) => {
                                        setTrailerUploaded(progress);
                                      },
                                      setTrailerAbort
                                    );
                                    setIsSubmitDisabled(false);

                                    if (uploadSuccessful) {
                                      setTrailerUploaded(true);
                                      toast.success(
                                        "Video uploaded and saved to distribution successfully!"
                                      );

                                      // Fetch updated distribution data to get new video details
                                      const { data: updatedDistribution } =
                                        await axios.get<FormSchema & Distribution>(
                                          "/client/distribution",
                                          {
                                            headers: {
                                              "X-Project-Id": filmId,
                                              "X-Project-Type": "film",
                                            },
                                          }
                                        );
                                      setDistribution(updatedDistribution);
                                    } else {
                                      setTrailerUploaded(false);
                                      setTrailer(null);
                                    }
                                  } else {
                                    setTrailer(null);
                                    setTrailerUploaded(null);
                                    if (trailerAbort) trailerAbort.abort();
                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "primary-trailer"
                                      );
                                      setDistribution(newDistribution);
                                    }
                                  }
                                }}
                                accept="video/mp4"
                              />
                            </DistributeFormItem>
                          </VideoField>
                          <VideoField>
                            <DistributeFormItem label="Additional Video" name="verticalVideo">
                              <FileUpload variant="dropzone"
                                dropzonePreview={distributionVideoPreview(
                                  "additional-video"
                                )}
                                file={
                                  verticalVideo instanceof File
                                    ? verticalVideo
                                    : null
                                }
                                fileUrl={
                                  distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "additional-video"
                                  )?.video_id
                                    ? `https://iframe.mediadelivery.net/embed/102339/${distribution.videos.find(
                                      (video) =>
                                        video.video_type === "additional-video"
                                    )!.video_id
                                    }?autoplay=false`
                                    : null
                                }
                                fileName={
                                  distribution?.videos.find(
                                    (video) =>
                                      video.video_type === "additional-video"
                                  )?.video_file_name ??
                                  (typeof verticalVideo === "string"
                                    ? verticalVideo
                                    : undefined)
                                }
                                uploaded={verticalVideoUploaded}
                                onChange={async (newTrailer) => {
                                  // Reset states when new file is selected
                                  if (newTrailer) {
                                    toast.success(
                                      "Uploading video... Please wait."
                                    );
                                    setVerticalVideoUploaded(0); // Start with 0 progress
                                    setVerticalVideo(newTrailer);

                                    // Clear existing video from distribution state
                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "additional-video"
                                      );
                                      setDistribution(newDistribution);
                                    }

                                    if (verticalVideoAbort) {
                                      verticalVideoAbort.abort();
                                    }

                                    if (!distributionId) return;

                                    setIsSubmitDisabled(true);
                                    const uploadSuccessful = await uploadFile(
                                      newTrailer,
                                      "vertical_video",
                                      (progress) => {
                                        setVerticalVideoUploaded(progress);
                                      },
                                      setVerticalVideoAbort
                                    );
                                    setIsSubmitDisabled(false);

                                    if (uploadSuccessful) {
                                      setVerticalVideoUploaded(true);
                                      toast.success(
                                        "Video uploaded and saved to distribution successfully!"
                                      );

                                      // Fetch updated distribution data to get new video details
                                      const { data: updatedDistribution } =
                                        await axios.get<FormSchema & Distribution>(
                                          "/client/distribution",
                                          {
                                            headers: {
                                              "X-Project-Id": filmId,
                                              "X-Project-Type": "film",
                                            },
                                          }
                                        );
                                      setDistribution(updatedDistribution);
                                    } else {
                                      setVerticalVideoUploaded(false);
                                      setVerticalVideo(null);
                                    }
                                  } else {
                                    // Handle file removal
                                    setVerticalVideo(null);
                                    setVerticalVideoAbort(null);
                                    if (verticalVideoAbort)
                                      verticalVideoAbort.abort();

                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "additional-video"
                                      );
                                      setDistribution(newDistribution);
                                    }
                                  }
                                }}
                                accept="video/mp4"
                              />
                            </DistributeFormItem>
                          </VideoField>
                          <VideoField>
                            <DistributeFormItem
                              label="Additional Trailer"
                              name="verticalFilmTrailer"
                            >
                              <FileUpload variant="dropzone"
                                dropzonePreview={distributionVideoPreview(
                                  "additional-trailer"
                                )}
                                file={
                                  verticalTrailer instanceof File
                                    ? verticalTrailer
                                    : null
                                }
                                fileUrl={
                                  distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "additional-trailer"
                                  )?.video_id
                                    ? `https://iframe.mediadelivery.net/embed/102339/${distribution.videos.find(
                                      (video) =>
                                        video.video_type ===
                                        "additional-trailer"
                                    )!.video_id
                                    }?autoplay=false`
                                    : null
                                }
                                fileName={
                                  distribution?.videos.find(
                                    (video) =>
                                      video.video_type === "additional-trailer"
                                  )?.video_file_name ??
                                  (typeof verticalTrailer === "string"
                                    ? verticalTrailer
                                    : undefined)
                                }
                                uploaded={verticalTrailerUploaded}
                                onChange={async (newTrailer) => {
                                  // Reset states when new file is selected
                                  if (newTrailer) {
                                    toast.success(
                                      "Uploading video... Please wait."
                                    );
                                    setVerticalTrailerUploaded(0); // Start with 0 progress
                                    setVerticalTrailer(newTrailer);

                                    // Clear existing video from distribution state
                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "additional-trailer"
                                      );
                                      setDistribution(newDistribution);
                                    }

                                    if (verticalTrailerAbort) {
                                      verticalTrailerAbort.abort();
                                    }

                                    if (!distributionId) return;

                                    setIsSubmitDisabled(true);
                                    const uploadSuccessful = await uploadFile(
                                      newTrailer,
                                      "vertical_trailer",
                                      (progress) => {
                                        setVerticalTrailerUploaded(progress);
                                      },
                                      setVerticalTrailerAbort
                                    );
                                    setIsSubmitDisabled(false);

                                    if (uploadSuccessful) {
                                      setVerticalTrailerUploaded(true);
                                      toast.success(
                                        "Video uploaded and saved to distribution successfully!"
                                      );

                                      // Fetch updated distribution data to get new video details
                                      const { data: updatedDistribution } =
                                        await axios.get<FormSchema & Distribution>(
                                          "/client/distribution",
                                          {
                                            headers: {
                                              "X-Project-Id": filmId,
                                              "X-Project-Type": "film",
                                            },
                                          }
                                        );
                                      setDistribution(updatedDistribution);
                                    } else {
                                      setVerticalTrailerUploaded(false);
                                      setVerticalTrailer(null);
                                    }
                                  } else {
                                    // Handle file removal
                                    setVerticalTrailer(null);
                                    setVerticalTrailerAbort(null);
                                    if (verticalTrailerAbort)
                                      verticalTrailerAbort.abort();

                                    if (distribution) {
                                      const newDistribution = { ...distribution };
                                      newDistribution.videos = (
                                        distribution.videos ?? []
                                      ).filter(
                                        (video) =>
                                          video.video_type !== "additional-trailer"
                                      );
                                      setDistribution(newDistribution);
                                    }
                                  }
                                }}
                                accept="video/mp4"
                              />
                            </DistributeFormItem>
                          </VideoField>
                        </VideoQuadGrid>
                      </UploadSection>
                      <UploadSection $gridArea="subtitles">
                        <UploadSectionTitle>Subtitles</UploadSectionTitle>
                        <UploadSectionHint>
                          Tell us how captions are delivered. Upload separate files
                          only if you are not burning them into the master.
                        </UploadSectionHint>
                        <FieldStack>
                          <DistributeFormItem
                            label="Subtitle options"
                            name="subtitle_status"
                            required
                            rules={[{ required: true }]}
                          >
                            <Select
                              size="large"
                              placeholder="How are subtitles provided?"
                              showSearch
                              options={[
                                { label: "Embedded in video", value: "embedded" },
                                {
                                  label: "Subtitle file is available",
                                  value: "available",
                                },
                                {
                                  label: "No subtitles available",
                                  value: "not_available",
                                },
                              ]}
                              onSelect={(value) => {
                                setIsSubtitlesAvailable(value === "available");
                                if (value === "available" && !subtitles.length) {
                                  setSubtitles([
                                    ...subtitles,
                                    { language: "", file: null },
                                  ]);
                                }
                              }}
                            />
                          </DistributeFormItem>
                        </FieldStack>
                        {isSubtitlesAvailable ? (
                          <FieldStack>
                            <DistributeFormItem
                              label="Subtitle files"
                              name="subtitleFiles"
                              required
                            >
                              <SubtitlesStrip>
                                {subtitles.map((subtitle, i) => (
                                  <Subtitle
                                    key={`Subtitle__${i}`}
                                    fileUploadVariant="dropzone"
                                    isDeletable={i !== 0}
                                    language={subtitle.language || undefined}
                                    file={
                                      subtitle.file instanceof File
                                        ? subtitle.file
                                        : typeof subtitle.file === "string"
                                          ? new File(
                                            [],
                                            subtitle.file?.split("/")?.at(-1) ?? ""
                                          )
                                          : null
                                    }
                                    onChange={(newSubtitle) => {
                                      const newSubtitles = [...subtitles];
                                      newSubtitles[i] = {
                                        ...subtitle,
                                        ...newSubtitle,
                                      };

                                      setSubtitles(newSubtitles);
                                    }}
                                    onRemove={() => {
                                      const newSubtitles = [...subtitles].filter(
                                        (_, j) => i !== j
                                      );

                                      setSubtitles(newSubtitles);
                                    }}
                                  />
                                ))}
                                <TextButton
                                  onClick={() =>
                                    subtitles.length >= 5
                                      ? null
                                      : setSubtitles([
                                        ...subtitles,
                                        { language: "", file: null },
                                      ])
                                  }
                                  leadingIcon={
                                    <MdAdd size={15} color={colors.primary.hex} />
                                  }
                                  disabled={subtitles.length >= 5}
                                >
                                  Add Subtitles File
                                </TextButton>
                              </SubtitlesStrip>
                            </DistributeFormItem>
                          </FieldStack>
                        ) : null}
                      </UploadSection>
                      <UploadSection $gridArea="documents">
                        <UploadSectionTitle>Documents</UploadSectionTitle>
                        <UploadSectionHint>
                          Optional uploads for certification and music reporting.
                          Skip if your territory does not require them.
                        </UploadSectionHint>
                        <DocPairGrid>
                          <DocCell>
                            <DistributeFormItem
                              label="Censor certificate"
                              name="censorCertificate"
                            >
                              <FileUpload variant="dropzone"
                                file={
                                  censorCertificate instanceof File
                                    ? censorCertificate
                                    : null
                                }
                                fileName={
                                  (censorCertificate instanceof File
                                    ? censorCertificate.name
                                    : censorCertificate) ?? undefined
                                }
                                fileUrl={
                                  censorCertificate instanceof File
                                    ? null
                                    : censorCertificate
                                }
                                onChange={(file) => {
                                  setCensorCertificate(file);
                                  file &&
                                    toast.success(
                                      "Added! File will upload once you press save."
                                    );
                                }}
                                uploaded={!!censorCertificate}
                              />
                            </DistributeFormItem>
                          </DocCell>
                          <DocCell>
                            <DistributeFormItem
                              label="Music cue sheet"
                              name="musicCueSheet"
                            >
                              <FileUpload variant="dropzone"
                                file={
                                  musicCueSheet instanceof File
                                    ? musicCueSheet
                                    : null
                                }
                                fileName={
                                  (musicCueSheet instanceof File
                                    ? musicCueSheet.name
                                    : musicCueSheet) ?? undefined
                                }
                                fileUrl={
                                  musicCueSheet instanceof File
                                    ? null
                                    : musicCueSheet
                                }
                                onChange={(file) => {
                                  setMusicCueSheet(file);
                                  file &&
                                    toast.success(
                                      "Added! File will upload once you press save."
                                    );
                                }}
                                uploaded={!!musicCueSheet}
                              />
                            </DistributeFormItem>
                          </DocCell>
                        </DocPairGrid>
                      </UploadSection>
                    </UploadBoard>
                  </UploadShell>
                </Form>
                <DistributeActions>
                  <SubmitButton
                    variant="inline"
                    label={
                      (typeof trailerUploaded === "number" &&
                        trailerUploaded < 1) ||
                        (typeof filmVideoUploaded === "number" &&
                          filmVideoUploaded < 1) ||
                        (typeof verticalTrailerUploaded === "number" &&
                          verticalTrailerUploaded < 1) ||
                        (typeof verticalVideoUploaded === "number" &&
                          verticalVideoUploaded < 1) ||
                        isSubmitDisabled
                        ? "Uploading..."
                        : filmVideoUploaded
                          ? "Submit"
                          : "Save"
                    }
                    disabled={
                      (typeof trailerUploaded === "number" &&
                        trailerUploaded < 1) ||
                      (typeof filmVideoUploaded === "number" &&
                        filmVideoUploaded < 1) ||
                      (typeof verticalTrailerUploaded === "number" &&
                        verticalTrailerUploaded < 1) ||
                      (typeof verticalVideoUploaded === "number" &&
                        verticalVideoUploaded < 1) ||
                      isSubmitDisabled ||
                      (isSubtitlesAvailable &&
                        !subtitles.filter(
                          (subtitle) => subtitle.language && subtitle.file
                        ).length)
                    }
                    onClick={async () => {
                      try {
                        const primarySaved = distribution?.videos?.some(
                          (video) =>
                            video.video_type === "primary-video" &&
                            !!(video.video_id || video.video_file_name)
                        );
                        if (!filmVideo && !primarySaved) {
                          return toast.error("Please upload a film video!");
                        }

                        form2.submit();
                        await form2.validateFields();

                        const data = {
                          subtitle_status: form2.getFieldValue("subtitle_status"),
                          distribution_id: distributionId,
                        };

                        const formData = jsonToFormData(data);
                        if (censorCertificate)
                          formData.append(
                            "censor_certificate",
                            censorCertificate
                          );
                        if (musicCueSheet)
                          formData.append("music_cue_sheet", musicCueSheet);

                        for (const subtitle of subtitles) {
                          if (!subtitle.file) continue;

                          const newName = v4() + ".srt";
                          const file =
                            subtitle.file instanceof File
                              ? new File([subtitle.file], newName)
                              : subtitle.file;

                          formData.append("subtitle_file", file);
                          formData.append(
                            "subtitle_metadata",
                            JSON.stringify({
                              name: newName,
                              language: subtitle.language,
                              ...(subtitle.id && typeof subtitle.file === "string"
                                ? { id: subtitle.id }
                                : {}),
                            })
                          );
                        }
                        if (!filmVideo) formData.append("delete_video", "true");
                        if (!verticalTrailer)
                          formData.append("delete_vertical_trailer", "true");
                        if (!trailer) formData.append("delete_trailer", "true");
                        if (!verticalVideo)
                          formData.append("delete_vertical_video", "true");

                        axios
                          .post("/client/distribution/additional-data", formData)
                          .then((resp) =>
                            handleResponse(resp, () =>
                              navigate(`/${film?.custom_name}`, {
                                replace: true,
                              })
                            )
                          )
                          .catch(handleResponse);
                      } catch (e) {
                        if (e instanceof AxiosError) {
                          handleResponse(e);
                        } else {
                          toast.error("Please fix the errors in the form!");
                        }
                      }
                    }}
                  />
                </DistributeActions>
              </>
            ),
          },
        ]}
      />
    </Main>
  );
};

const DISTRIBUTE_PAGE_MAX = "min(94vw, 172rem)";

const Main = styled.main`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100dvh;
  padding: 8.5rem clamp(1.2rem, 3vw, 3.2rem) 2.4rem  clamp(1.2rem, 3vw, 3.2rem);
  padding-right: 8rem;
  width: 100%;
  max-width: 100%;
  background-color: ${colors.lightBackground.hex};

  .distribute-tabs.ant-tabs {
    flex: 1 1 auto;
    min-height: min-content;
    display: flex !important;
    flex-direction: column;
    max-width: ${DISTRIBUTE_PAGE_MAX};
    width: 100%;
    margin: 0 auto;
  }

  .distribute-tabs.ant-tabs .ant-tabs-content-holder {
    flex: 1 1 auto;
    min-height: min-content;
    overflow: visible;
  }

  .distribute-tabs.ant-tabs .ant-tabs-content,
  .distribute-tabs.ant-tabs .ant-tabs-tabpane {
    height: auto;
    overflow: visible;
  }

  .distribute-tabs.ant-tabs .ant-tabs-tabpane-active {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: min-content;
    overflow: visible;
  }

  /* Reinforce vertical stacking inside nested Ant rows */
  .distribute-form.ant-form-vertical .ant-form-item:not(.ant-form-item-horizontal)
    .ant-form-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .distribute-form .ant-form-item-vertical .ant-form-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .distribute-form .ant-form-item-control-input-content {
    width: 100%;
  }

  .distribute-form.ant-form .ant-form-item .ant-checkbox-wrapper {
    white-space: normal;
    align-items: flex-start;
  }

  .distribute-tabs .ant-tabs-nav {
    margin-bottom: 0.6rem;
    flex-shrink: 0;
  }

  .distribute-tabs .ant-tabs-nav::before {
    border-bottom: none;
  }

  .distribute-tabs .ant-tabs-tab {
    font-size: 1.65rem;
    padding: 0.55rem 0;
    color: ${colors.text.hex};
    opacity: 0.55;
  }

  .distribute-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${colors.text.hex};
    font-weight: 600;
    opacity: 1;
  }

  .distribute-tabs .ant-tabs-tab-disabled {
    opacity: 0.35;
  }

  .distribute-tabs .ant-tabs-ink-bar {
    height: 3px;
    border-radius: 999px;
    background: ${colors.primary.hex};
  }

  .distribute-form.ant-form {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .distribute-form.ant-form .ant-form-item {
    margin-bottom: 1.6rem !important;
  }

  .distribute-rights-form.ant-form .ant-form-item {
    margin-bottom: 0.85rem !important;
  }

  .distribute-rights-form .ant-form-item-label {
    padding-bottom: 0.15rem !important;
  }

  .distribute-uploads,
  .distribute-uploads .ant-form-item,
  .distribute-uploads .ant-form-item-row,
  .distribute-uploads .ant-form-item-label,
  .distribute-uploads .ant-form-item-control,
  .distribute-uploads .ant-form-item-control-input,
  .distribute-uploads .ant-form-item-control-input-content,
  .distribute-video-quad-grid,
  .distribute-video-quad-grid > * {
    min-width: 0 !important;
    max-width: 100%;
  }

  .distribute-uploads .ant-form-item {
    margin-bottom: 0 !important;
    width: 100%;
  }

  .distribute-uploads .ant-form-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .distribute-uploads .ant-form-item-label,
  .distribute-uploads .ant-form-item-control,
  .distribute-uploads .ant-col {
    max-width: 100% !important;
    width: 100% !important;
    flex: 0 1 auto !important;
  }

  .distribute-uploads .ant-form-item-label > label {
    white-space: normal !important;
    line-height: 1.3;
  }

  .distribute-uploads .ant-form-item-control-input-content > div {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: auto;
    min-height: 12rem !important;
    flex: 1 1 auto;
    box-sizing: border-box;
    padding: 1.2rem 0.8rem !important;
  }

  .distribute-uploads .ant-form-item-control-input-content > div > div {
    min-width: 0;
    max-width: 100%;
    padding-left: 0.3rem !important;
    padding-right: 0.3rem !important;
  }

  .distribute-uploads .ant-form-item-control-input-content p {
    white-space: normal;
    overflow-wrap: break-word;
  }

  .distribute-form.ant-form .ant-form-item-label > label {
    font-size: 1.35rem !important;
    font-weight: 600 !important;
    color: ${colors.text.hex} !important;
    height: auto !important;
  }

  .distribute-rights-form .ant-checkbox-wrapper {
    font-size: 1.35rem;
    line-height: 1.45;
    align-items: flex-start;
    color: ${colors.text.hex};
  }

  .distribute-rights-form .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-top: 0;
    margin-inline-start: 0 !important;
  }

  .distribute-rights-form .rights-ack .ant-form-item {
    margin-bottom: 0.35rem !important;
  }

  .distribute-rights-form .rights-ack .ant-form-item:has(.ant-input) {
    margin-top: 0.7rem;
    margin-bottom: 1rem !important;
  }

  @media (max-width: 72em), (max-height: 40em) {
    padding-bottom: 3rem;
  }

  .ant-form-item-has-error {
    margin-bottom: 2.4rem !important;
  }
`;

const DistributeHero = styled.div`
  max-width: ${DISTRIBUTE_PAGE_MAX};
  width: 100%;
  margin: 0 auto 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  flex-shrink: 0;
`;

const HeroTop = styled(Row).attrs({ $full: true })`
  width: 100%;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1.6rem 2rem;
`;

const Title = styled.h1`
  margin: 0;
  flex: 1;
  min-width: min(100%, 28rem);
  font-size: clamp(2.6rem, 4vw, 3.8rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${colors.text.hex};
`;

const FilmName = styled.span`
  font-weight: 600;
  color: ${colors.primary.hex};
`;

const Paragraph = styled.p`
  margin: 0;
  max-width: min(96rem, 100%);
  font-size: 1.55rem;
  line-height: 1.55;
  font-weight: 400;
  color: ${colors.text.hex};
  opacity: 0.82;
`;

const RightsShell = styled.div`
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 0 auto;
  padding: 1.4rem 1.8rem 1.2rem;
  background-color: ${colors.background.hex};
  border: 1px solid ${colors.grey.hex};
  border-radius: 1.2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 1.6rem;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  min-height: min-content;
  align-items: stretch;

  @media (max-width: 72em), (max-height: 40em) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const RightsColumn = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const AckPanel = styled.div.attrs({ className: "rights-ack" })`
  flex: 1 1 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.4rem 1.6rem 1.8rem;
  border-radius: 0.9rem;
  background: ${colors.isDark ? "#02060b" : "#f4f7fb"};
  border: 1px solid ${colors.isDark ? "rgba(242, 247, 252, 0.1)" : colors.grey.hex};
`;

const AckHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.9rem;
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${colors.text.hex};
`;

const RequiredStar = styled.span`
  color: ${colors.error.hex};
  font-size: 1.35rem;
  line-height: 1;
`;

const RightsSubmit = styled.div`
  margin-top: 0.4rem;
  display: flex;
  justify-content: center;

  button {
    margin: 0;
    width: auto;
    min-width: min(100%, 24rem);
    max-width: 100%;
  }
`;

const UploadShell = styled.div`
  width: 100%;
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 0 auto;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const UploadBoard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(0, 0.72fr) minmax(0, 0.9fr);
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: "videos subtitles documents";
  gap: 1.2rem;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  align-items: stretch;

  @media (max-width: 80em) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    height: auto;
    grid-template-areas:
      "videos"
      "subtitles"
      "documents";
  }
`;

const UploadSection = styled.section<{ $gridArea: "videos" | "subtitles" | "documents" }>`
  grid-area: ${({ $gridArea }) => $gridArea};
  width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 1.2rem 1.4rem;
  background-color: ${colors.background.hex};
  border: 1px solid ${colors.grey.hex};
  border-radius: 1.2rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: visible;

  ${({ $gridArea }) =>
    $gridArea === "videos"
      ? `
    .distribute-video-quad-grid {
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
    }
  `
      : $gridArea === "documents"
        ? `
    & > div:last-child {
      flex: 1 1 auto;
      min-height: 0;
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    }
  `
        : ""}
`;

const UploadSectionTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.55rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: 0.01em;
`;

const UploadSectionHint = styled.p`
  margin: 0 0 0.8rem;
  font-size: 1.3rem;
  line-height: 1.5;
  color: ${colors.text.hex};
  opacity: 0.78;
  max-width: 100%;
`;

const VideoQuadGrid = styled.div.attrs({
  className: "distribute-video-quad-grid",
})`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: auto auto;
  gap: 1rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 48em) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
  }
`;

const VideoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  padding: 0;
  border-radius: 0;
  border: none;
  background: transparent;

  /* Beat DistributeFormItem's flex: none !important so rows can stretch equally */
  && .ant-form-item {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column;
    min-height: 0;
    margin-bottom: 0 !important;
  }

  && .ant-form-item-row {
    flex: 1 1 auto !important;
    flex-direction: column !important;
    min-height: 0 !important;
    align-items: stretch !important;
    max-width: 100%;
  }

  && .ant-form-item-control {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
  }

  && .ant-form-item-control-input,
  && .ant-form-item-control-input-content {
    flex: 1 1 auto !important;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  && .ant-form-item-label {
    padding-bottom: 0.45rem;
    flex-shrink: 0;
  }
`;

const DocPairGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 72em) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: auto;
  }
`;

const DocCell = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;

  .ant-form-item {
    margin-bottom: 0 !important;
    flex: 1 1 auto;
    display: flex !important;
    flex-direction: column;
    min-height: 0;
  }

  .ant-form-item-row,
  .ant-form-item-control,
  .ant-form-item-control-input,
  .ant-form-item-control-input-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 26rem;
  border-radius: 0.85rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid ${colors.grey.hex};

  & iframe,
  & video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const SubtitlesStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.2rem;
  width: 100%;
  padding: 1.2rem 0;
`;

const DistributeActions = styled.div`
  max-width: ${DISTRIBUTE_PAGE_MAX};
  margin: 0.8rem auto 0;
  padding-bottom: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  flex-shrink: 0;
`;

const ProcessingText = styled.h3`
  font-size: 1.8rem;
  animation: blink 3s infinite both;

  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;
